import { useState, useEffect, useRef } from 'react';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function apiToken() { return localStorage.getItem('token'); }

export default function PhotoUpload({ contextType, contextId, photoType = 'general', label = 'Photos', canUpload = true }) {
  const [photos, setPhotos]     = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox]  = useState(null);
  const [caption, setCaption]    = useState('');
  const [uploadError, setUploadError] = useState(null);
  const fileRef = useRef();

  const load = () => {
    if (!contextId) return;
    fetch(`${API}/photos?context_type=${contextType}&context_id=${contextId}`, {
      headers: { Authorization: `Bearer ${apiToken()}` },
    }).then(r => r.json()).then(d => setPhotos(Array.isArray(d) ? d : []));
  };

  useEffect(() => { load(); }, [contextType, contextId]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('context_type', contextType);
    fd.append('context_id',   String(contextId));
    fd.append('photo_type',   photoType);
    if (caption.trim()) fd.append('caption', caption.trim());

    try {
      const res = await fetch(`${API}/photos/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) { setUploadError(data.error || `Upload failed (${res.status})`); }
      else { setCaption(''); load(); }
    } catch (err) {
      setUploadError('Network error — could not reach server');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (photo) => {
    if (!window.confirm('Delete this photo?')) return;
    await fetch(`${API}/photos/${photo.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${apiToken()}` },
    });
    load();
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>{label}</div>

      {/* Photo grid */}
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, marginBottom: 12 }}>
          {photos.map(p => (
            <div key={p.id} style={{ position: 'relative', borderRadius: 7, overflow: 'hidden', border: '1px solid #334155', backgroundColor: '#0f172a', cursor: 'pointer' }}
              onClick={() => setLightbox(p)}>
              <img
                src={`${API}/uploads/${p.filename}`}
                alt={p.caption || p.original_name}
                style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
              />
              {p.caption && (
                <div style={{ padding: '4px 6px', fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.caption}</div>
              )}
              {canUpload && (
                <button
                  onClick={e => { e.stopPropagation(); remove(p); }}
                  style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.7)', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload controls */}
      {canUpload && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            placeholder="Caption (optional)"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            style={{ flex: 1, padding: '7px 10px', backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 12 }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{ padding: '7px 14px', backgroundColor: uploading ? '#374151' : '#1e3a5f', color: uploading ? '#64748b' : '#93c5fd', border: '1px solid #1d4ed8', borderRadius: 7, cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
            {uploading ? 'Uploading…' : '📷 Add Photo'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
        </div>
      )}

      {photos.length === 0 && !canUpload && (
        <div style={{ color: '#475569', fontSize: 13, fontStyle: 'italic' }}>No photos uploaded.</div>
      )}

      {uploadError && (
        <div style={{ marginTop: 8, color: '#f87171', fontSize: 12, backgroundColor: '#1c0a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '6px 10px' }}>
          ✗ {uploadError}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <img src={`${API}/uploads/${lightbox.filename}`} alt={lightbox.caption || ''} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {lightbox.caption && <div style={{ color: '#e2e8f0', fontSize: 14 }}>{lightbox.caption}</div>}
                <div style={{ color: '#64748b', fontSize: 12 }}>{lightbox.uploaded_by_name} · {new Date(lightbox.uploaded_at).toLocaleString()}</div>
              </div>
              <button onClick={() => setLightbox(null)} style={{ padding: '6px 14px', backgroundColor: '#334155', color: '#94a3b8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
